/**
 * DVRZI cart drawer — extra behaviour for the custom blocks
 * (coupon code toggle/apply, upsell quick-add, rewards bar animation).
 *
 * Line item quantity/remove and the drawer open/close/auto-open flow are
 * handled by the theme's native cart.js + cart-drawer.js — this file only
 * adds behaviour for the new blocks and is intentionally independent of
 * them so it degrades gracefully if a block is removed in the editor.
 */
document.addEventListener('click', function (event) {
  var toggle = event.target.closest('[data-cart-drawer-coupon-toggle]');
  if (toggle) {
    var form = document.getElementById('CartDrawer-CouponForm');
    if (!form) return;
    var isHidden = form.hasAttribute('hidden');
    if (isHidden) {
      form.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      var input = document.getElementById('CartDrawer-CouponInput');
      if (input) input.focus();
    } else {
      form.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    }
    return;
  }

  var applyButton = event.target.closest('[data-cart-drawer-apply-discount]');
  if (applyButton) {
    var codeInput = document.getElementById('CartDrawer-CouponInput');
    var code = codeInput ? codeInput.value.trim() : '';
    if (!code) {
      if (codeInput) codeInput.focus();
      return;
    }
    applyButton.disabled = true;
    applyButton.textContent = 'Applying…';
    cartDrawerApplyDiscount(code, {
      onDone: function () {
        applyButton.disabled = false;
        applyButton.textContent = 'Apply';
      },
    });
    return;
  }

  var quickAddButton = event.target.closest('[data-cart-drawer-quick-add]');
  if (quickAddButton) {
    event.preventDefault();
    cartDrawerQuickAdd(quickAddButton);
    return;
  }

  var couponCardApply = event.target.closest('[data-cart-drawer-apply-code]');
  if (couponCardApply && !couponCardApply.disabled) {
    var couponCode = couponCardApply.getAttribute('data-cart-drawer-apply-code');
    if (!couponCode) return;
    couponCardApply.classList.add('is-applying');
    couponCardApply.textContent = 'Applying…';
    cartDrawerApplyDiscount(couponCode);
  }
});

/**
 * Apply a discount code to the CART (not straight to checkout) so it shows
 * up immediately in Price Details and stays applied — across browsing, more
 * items added, etc — until the customer manually goes to checkout, where
 * Shopify carries the same code forward automatically.
 *
 * /discount/<code> is Shopify's only theme-level way to apply a code without
 * an app, and it normally requires a full top-level page navigation (it sets
 * the discount, then 302-redirects). That was jarring for the free-gift
 * flow especially — crossing a rewards threshold would blow away whatever
 * page the customer was on. Instead we load that same URL inside a hidden,
 * same-origin iframe: the Set-Cookie response lands in the browser's normal
 * cookie jar the instant the iframe finishes loading, visible to the
 * top-level page, without ever navigating the visible tab. Once that's
 * done we just re-fetch the cart drawer's own sections and swap them in —
 * the same in-place refresh a normal cart change does — so the customer
 * sees the discount applied with no reload at all.
 *
 * Falls back to the old full-page redirect if the iframe doesn't report
 * success in time (e.g. a future Shopify change blocks same-origin framing
 * on this route), so the discount is never silently left unapplied.
 *
 * @param {string} code
 * @param {{onDone?: () => void}} [options] - onDone fires once the discount
 *   has been applied and the drawer refreshed (or once the fallback
 *   redirect has been kicked off, in which case the page is about to
 *   unload anyway).
 */
function cartDrawerApplyDiscount(code, options) {
  options = options || {};
  var onDone = options.onDone;
  var settled = false;

  function fallbackToRedirect() {
    if (settled) return;
    settled = true;
    try {
      sessionStorage.setItem('dvrziReopenCartDrawer', '1');
    } catch (e) {}
    var returnTo = window.location.pathname + window.location.search;
    window.location.href =
      window.location.origin + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(returnTo);
  }

  try {
    var iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src =
      window.location.origin + '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart');

    var timeoutId = setTimeout(fallbackToRedirect, 6000);

    iframe.onload = function () {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);

      // Give the redirect chain inside the iframe a beat to fully settle
      // before we trust the cookie it set, then refresh the drawer's
      // sections in place — no navigation happened, so unlike the old
      // redirect flow there's nothing to "reopen" afterward.
      setTimeout(function () {
        if (iframe.parentNode) iframe.remove();

        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          var ids = cartDrawer.getSectionsToRender().map(function (section) {
            return section.id;
          });
          fetch(window.location.pathname + '?sections=' + ids.join(','))
            .then(function (response) {
              return response.json();
            })
            .then(function (sections) {
              if (typeof cartDrawer.renderContents === 'function') {
                cartDrawer.renderContents({ sections: sections });
              }
              if (onDone) onDone();
            })
            .catch(function (error) {
              console.error(error);
              if (onDone) onDone();
            });
        } else if (onDone) {
          onDone();
        }
      }, 250);
    };

    document.body.appendChild(iframe);
  } catch (e) {
    console.error(e);
    fallbackToRedirect();
  }
}

document.addEventListener('DOMContentLoaded', function () {
  var shouldReopen = false;
  try {
    shouldReopen = sessionStorage.getItem('dvrziReopenCartDrawer') === '1';
    if (shouldReopen) sessionStorage.removeItem('dvrziReopenCartDrawer');
  } catch (e) {}

  if (!shouldReopen) return;

  var cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    try {
      cartDrawer.open();
      return;
    } catch (e) {}
  }
  var drawerEl = document.querySelector('.drawer');
  if (drawerEl) drawerEl.classList.add('active');
});

function cartDrawerQuickAdd(button) {
  if (button.classList.contains('is-loading')) return;

  var variantId = button.getAttribute('data-variant-id');
  if (!variantId) return;

  var cartDrawer = document.querySelector('cart-drawer');

  button.classList.add('is-loading');
  button.disabled = true;

  var formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);

  if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
    formData.append(
      'sections',
      cartDrawer.getSectionsToRender().map(function (section) {
        return section.id;
      })
    );
    formData.append('sections_url', window.location.pathname);
  }

  fetch(window.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
    body: formData,
  })
    .then(function (response) {
      return response.json();
    })
    .then(function (data) {
      if (data.status) {
        button.classList.remove('is-loading');
        button.disabled = false;
        return;
      }

      button.classList.remove('is-loading');
      button.classList.add('is-added');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.setActiveElement && cartDrawer.setActiveElement(document.activeElement);
        cartDrawer.renderContents(data);
      } else {
        window.location.href = window.routes.cart_url;
      }
    })
    .catch(function (error) {
      console.error(error);
      button.classList.remove('is-loading');
      button.disabled = false;
    });
}

/**
 * Animate the rewards-bar fill from its last known width to its current
 * target width. The drawer re-renders by swapping in fresh HTML on every
 * cart change, so a plain CSS transition never has a "before" state to
 * animate from — we supply one ourselves (remembered in sessionStorage) so
 * the green bar visibly grows/shrinks and glides past checkpoint dots
 * instead of jumping straight to its new position.
 */
function cartDrawerAnimateRewardsFill() {
  var fill = document.querySelector('.cart-drawer__rewards-fill[data-target-width]');
  if (!fill) return;

  var target = parseFloat(fill.getAttribute('data-target-width'));
  if (isNaN(target)) return;

  var previous = 0;
  try {
    var stored = sessionStorage.getItem('dvrziRewardsFillPct');
    if (stored !== null) previous = parseFloat(stored) || 0;
  } catch (e) {}

  fill.style.transition = 'none';
  fill.style.width = previous + '%';
  // Force layout so the browser registers the starting width before we
  // animate to the target — without this the two width assignments would
  // collapse into one and nothing would visibly move.
  void fill.offsetWidth;

  requestAnimationFrame(function () {
    fill.style.transition = '';
    fill.style.width = target + '%';
  });

  try {
    sessionStorage.setItem('dvrziRewardsFillPct', String(target));
  } catch (e) {}
}

/**
 * Free-gift auto-add. Once a rewards tier configured as "free_gift" is
 * unlocked, snippets/cart-drawer-block.liquid server-renders a
 * [data-cart-drawer-auto-gift] placeholder (instead of the old manual
 * "Add free gift" button) — we scan for it after every drawer render and add
 * the product straight away, no click required. The placeholder is gated
 * server-side on the gift not already being in the cart, so once the add
 * succeeds and the drawer re-renders, the placeholder simply stops being
 * rendered and this function finds nothing to do. If the customer removes
 * the gift by hand while the tier is still unlocked, it's simply re-added on
 * the next render, same as any other unlocked reward — and if they instead
 * remove OTHER items and drop back under the threshold, cartDrawerAutoRemoveGifts()
 * below takes the gift back out again.
 *
 * Making the gift actually FREE: Shopify's "Automatic discount" type — the
 * obvious native fit — turned out to silently refuse to apply on this store
 * whenever it combined "specific product" targeting with a minimum-purchase
 * requirement (confirmed both for automatic discounts and for discount
 * CODES; removing just the minimum-purchase requirement was the one change
 * that made it work in testing). So instead this uses a discount CODE
 * (data-discount-code, from the tier's "discount code" setting in the theme
 * editor) that is 100% off the gift product with NO minimum-purchase
 * requirement of its own — the ₹-threshold gating happens entirely here in
 * the theme (the Liquid above only renders this element once the cart total
 * clears the tier), not in Shopify's discount rules. Applying a code still
 * requires the theme's only code-application mechanism, /discount/<code>,
 * which cartDrawerApplyDiscount below now does via a hidden iframe so it
 * doesn't reload the page.
 *
 * Keeping it free (not just free once): every add here is tagged with a
 * hidden `_dvrzi_free_gift` line item property (underscore-prefixed, so
 * Shopify hides it from the customer-facing cart). snippets/cart-drawer.liquid
 * and sections/main-cart-items.liquid key off that property to render a
 * locked "Free gift" badge instead of a quantity stepper for that line, so
 * there's no +/- control that could take it above qty 1 — since the discount
 * code is a plain 100%-off-this-product code with no BXGY-style quantity cap
 * of its own, Shopify would otherwise happily discount as many units as sat
 * in that line. cartDrawerEnforceGiftQuantity() below is the backstop for
 * the (non-UI) case where quantity still ends up above 1, e.g. a direct
 * /cart/change.js call.
 */
var dvrziGiftsInFlight = {};

function cartDrawerAutoAddGifts() {
  var giftEls = document.querySelectorAll('[data-cart-drawer-auto-gift]');
  if (!giftEls.length) return;

  var cartDrawer = document.querySelector('cart-drawer');

  giftEls.forEach(function (el) {
    var variantId = el.getAttribute('data-variant-id');
    if (!variantId || dvrziGiftsInFlight[variantId]) return;
    dvrziGiftsInFlight[variantId] = true;

    var discountCode = el.getAttribute('data-discount-code');

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('properties[_dvrzi_free_gift]', '1');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
    }

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          // e.g. the gift went out of stock — don't retry it this pageview.
          delete dvrziGiftsInFlight[variantId];
          console.error('DVRZI free-gift auto-add failed:', data);
          return;
        }

        if (discountCode) {
          // Leave dvrziGiftsInFlight set until the discount is actually
          // applied (or the fallback redirect kicks off) so this can't fire
          // twice while that's in progress.
          cartDrawerApplyDiscount(discountCode, {
            onDone: function () {
              delete dvrziGiftsInFlight[variantId];
            },
          });
          return;
        }

        delete dvrziGiftsInFlight[variantId];
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
        }
      })
      .catch(function (error) {
        delete dvrziGiftsInFlight[variantId];
        console.error(error);
      });
  });
}

/**
 * Free-gift auto-remove. The reverse of cartDrawerAutoAddGifts(): if the
 * customer removes other items after a gift was already added and the cart
 * drops back under that tier's threshold, cart-drawer-block.liquid renders a
 * [data-cart-drawer-auto-remove-gift] placeholder for that line (it only
 * ever targets lines tagged _dvrzi_free_gift, so a unit of the same product
 * bought at full price is never touched). We scan for it after every drawer
 * render and pull the line back out — same in-place section refresh as
 * everything else here, no page reload — so a customer can't drop under the
 * threshold and still walk away with the gift.
 */
var dvrziGiftRemovalsInFlight = {};

function cartDrawerAutoRemoveGifts() {
  var removeEls = document.querySelectorAll('[data-cart-drawer-auto-remove-gift]');
  if (!removeEls.length) return;

  var cartDrawer = document.querySelector('cart-drawer');

  removeEls.forEach(function (el) {
    var lineKey = el.getAttribute('data-line-key');
    if (!lineKey || dvrziGiftRemovalsInFlight[lineKey]) return;
    dvrziGiftRemovalsInFlight[lineKey] = true;

    var body = { id: lineKey, quantity: 0 };
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      body.sections = cartDrawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        delete dvrziGiftRemovalsInFlight[lineKey];
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          cartDrawer.renderContents(data);
        }
      })
      .catch(function (error) {
        delete dvrziGiftRemovalsInFlight[lineKey];
        console.error('DVRZI free-gift auto-remove failed:', error);
      });
  });
}

/**
 * Backstop for the free-gift quantity lock. The Liquid templates render a
 * locked "Free gift" badge instead of a quantity stepper for any line
 * tagged `_dvrzi_free_gift`, so there's normally no control in the UI that
 * could push its quantity above 1 — this only catches the case where
 * quantity got there anyway (e.g. a direct /cart/change.js call bypassing
 * the drawer entirely). It reads quantity straight off the server-rendered
 * data-gift-quantity attribute (no extra /cart.js fetch needed) and, if a
 * gift line is over-quantity, corrects it back to 1 via /cart/change.js and
 * refreshes the drawer — same in-place refresh cartDrawerApplyDiscount uses,
 * no page reload.
 */
function cartDrawerEnforceGiftQuantity() {
  var giftLines = document.querySelectorAll('[data-free-gift="true"]');
  if (!giftLines.length) return;

  var offender = null;
  for (var i = 0; i < giftLines.length; i++) {
    var qty = parseInt(giftLines[i].getAttribute('data-gift-quantity'), 10);
    if (qty > 1) {
      offender = giftLines[i];
      break;
    }
  }
  if (!offender) return;

  var lineKey = offender.getAttribute('data-line-key');
  if (!lineKey) return;

  var cartDrawer = document.querySelector('cart-drawer');
  var body = { id: lineKey, quantity: 1 };
  if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
    body.sections = cartDrawer.getSectionsToRender().map(function (section) {
      return section.id;
    });
    body.sections_url = window.location.pathname;
  }

  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then(function (response) {
      return response.json();
    })
    .then(function (data) {
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
        cartDrawer.renderContents(data);
      }
    })
    .catch(function (error) {
      console.error('DVRZI free-gift quantity enforcement failed:', error);
    });
}

(function () {
  var reanimateTimer = null;
  function scheduleRewardsAnimate() {
    if (reanimateTimer) clearTimeout(reanimateTimer);
    reanimateTimer = setTimeout(function () {
      cartDrawerAnimateRewardsFill();
      cartDrawerAutoAddGifts();
      cartDrawerAutoRemoveGifts();
      cartDrawerEnforceGiftQuantity();
    }, 30);
  }

  document.addEventListener('DOMContentLoaded', function () {
    cartDrawerAnimateRewardsFill();
    cartDrawerAutoAddGifts();
    cartDrawerAutoRemoveGifts();
    cartDrawerEnforceGiftQuantity();

    var drawerEl = document.querySelector('cart-drawer');
    if (!drawerEl || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(scheduleRewardsAnimate);
    observer.observe(drawerEl, { childList: true, subtree: true });
  });
})();
